import ControlledModal from "@src/components/modal/ControlledModal";
import { useAuth } from "@src/routes/authProvider";
import { settingsActions } from "@src/store";
import { useAppDispatch, useAppSelector } from "@src/store/hooks";
import { Theme } from "@src/store/settingsSlice";
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getSettingsItems, getSupportItems, INavLeaf, INavNode } from "./navItems";
import { NavMenu } from "./NavMenu";
import styles from "./UserMenu.module.scss";

const LOGOUT_ID = "logout";

export const getUserInitials = (email: string, nameId: string | null): string => {
	const source = (email || nameId || "").trim();
	if (!source) return "?";

	const local = source.split("@")[0];
	const parts = local.split(/[.\-_\s]+/).filter(Boolean);

	if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
	return local.slice(0, 2).toUpperCase();
};

export const UserMenu: React.FC = () => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const { logout } = useAuth();

	const email = useAppSelector((state) => state.settings.email);
	const nameId = useAppSelector((state) => state.settings.nameId);
	const companyName = useAppSelector((state) => state.settings.companyName);

	const [isLogoutOpen, setIsLogoutOpen] = useState(false);

	const initials = useMemo(() => getUserInitials(email, nameId), [email, nameId]);

	const items: Array<INavNode> = useMemo(
		() => [
			{
				id: "themes",
				label: "Themes",
				children: [
					{ id: "light", label: "Light", onClick: () => dispatch(settingsActions.changeTheme(Theme.Light)) },
					{ id: "dark", label: "Dark", onClick: () => dispatch(settingsActions.changeTheme(Theme.Dark)) },
				],
			},
			{ id: "settings", label: "Settings", children: getSettingsItems() },
			{ id: "support", label: "Support", children: getSupportItems() },
			{ id: LOGOUT_ID, label: "Logout" },
		],
		[dispatch],
	);

	const handleSelect = (item: INavLeaf) => {
		if (item.id === LOGOUT_ID) {
			setIsLogoutOpen(true);
			return;
		}

		if (item.onClick) {
			item.onClick();
			return;
		}

		if (item.href) {
			window.open(item.href, "_blank");
			return;
		}

		if (item.to) navigate(item.to);
	};

	const handleLogoutClose = (proceed: boolean) => {
		setIsLogoutOpen(false);
		if (proceed) logout();
	};

	return (
		<>
			<NavMenu
				label="Account"
				items={items}
				align="right"
				ariaLabel="Account menu"
				menuClassName={styles.menu}
				onSelect={handleSelect}
				trigger={
					<span className={styles.avatar} title={email || nameId || "Account"}>
						{initials}
					</span>
				}
			/>

			<ControlledModal
				isOpen={isLogoutOpen}
				onClose={handleLogoutClose}
				headerText="Confirm Logout"
				title="Are you sure?"
				content={`You are about to sign out${companyName ? ` of ${companyName}` : ""}. Any unsaved changes will be lost.`}
				cancelButtonText="Cancel"
				okButtonText="Log out"
			/>
		</>
	);
};
